import React, {useCallback, useContext, useEffect} from 'react';
import {useFormContext} from "react-hook-form";
import {useDropzone} from "react-dropzone";
import makeStyles from '@mui/styles/makeStyles';
import {Card, CardContent, CardHeader, Tooltip, Typography} from "@mui/material";
import {BaseStyle} from "../../../../styles/shared/BaseStyle";
import clsx from "clsx";
import IconButton from "@mui/material/IconButton";
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import {setOpenSnackbar} from "../../../../services/AppServices";
import {AppContext} from "../../../../App";
import {languages} from "../../../../strings/LanguageDictionary";

const useStyles = makeStyles((theme) => ({
	container: {
		backgroundColor: "rgba(255, 255, 255, 0)",
		border: "3px dashed #304ffe",
		borderRadius: 22,
		minHeight: 160,
	},
	containerCompact: {
		border: "1px dashed var(--atl-border-strong)",
		borderRadius: 14,
		minHeight: 68,
		background: "var(--atl-surface-muted)",
	},
	dropzone: {
		textAlign: "center",
		padding: theme.spacing(2)
	},
	dropzoneCompact: {
		padding: theme.spacing(1),
		textAlign: "center",
	},
	aside: {
		marginTop: -40,
		"& ul": {
			listStyle: "circle",
			"& li": {
				padding: 8,
			}
		}
	}
}));

const FileInput = ({name, multiple, accept = "*", compact = false}) => {
	const classes = useStyles();
	const baseClasses = BaseStyle();
	const appContext = useContext(AppContext);
	const {register, unregister, setValue, watch, getValues} = useFormContext();
	const files = watch(name);
	
	const onDrop = useCallback(droppedFiles => {
		let allOk = true;
		droppedFiles.forEach((file) => {
			if (file.type === "") {
				allOk = false;
				setOpenSnackbar("warning", "Attenzione: Tipo di file non supportato!", appContext.dispatch);
			} else if (file.size >= 500000000) {
				allOk = false;
				setOpenSnackbar("warning", "Attenzione: Il file supera la dimensiona massima supportata di 500MB!", appContext.dispatch);
			}
		})
		if (allOk) {
			if (getValues()[name]) {
				if (multiple) {
					setValue(name, [...getValues()[name], ...droppedFiles], {shouldValidate: true})
				} else {
					setValue(name, [...droppedFiles], {shouldValidate: true})
				}
			} else {
				setValue(name, droppedFiles, {shouldValidate: true})
			}
		}
	}, [setValue, name, getValues, multiple, appContext.dispatch])
	
	const {getRootProps, getInputProps} = useDropzone({
		onDrop,
		multiple: multiple,
		acceptFiles: accept
	})
	
	const removeFile = useCallback((event, file) => {
		event.stopPropagation();
		let files = getValues()[name];
		files = files.filter(f => f.path !== file.path)
		setValue(name, files, {shouldValidate: true})
	}, [getValues, name, setValue])
	
	useEffect(() => {
		register(name)
		return () => {
			unregister(name)
		}
	}, [register, unregister, name])
	
	return (
		<React.Fragment>
			<div className={clsx(classes.container, compact && classes.containerCompact)}>
				<div
					{...getRootProps({className: clsx(classes.dropzone, compact && classes.dropzoneCompact)})}
					style={{minHeight: compact ? 68 : 160}}
				>
					<input
						type={"file"}
						accept={accept}
						multiple={multiple}
						id={name}
						name={name}
						{...getInputProps()}
					/>
					<div>
						<Tooltip
							title={languages[appContext.state.lang].TICKET_INPUT__FILE__TOOLTIP__}>
							<Typography variant={compact ? "body2" : "body1"}>
								{multiple ? languages[appContext.state.lang].TICKET_INPUT_FILE__MULTIPLE__ : languages[appContext.state.lang].TICKET_INPUT__FILE__SINGLEF__}
							</Typography>
						</Tooltip>
						{!!files?.length && (
							<div className={baseClasses.flexStart}>
								{files.map((file, index) => {
									return (
										<Card variant={"outlined"} key={index}
										      style={file.size >= 500000000 ? {backgroundColor: "#bd3030"} : {}}
										      className={clsx(baseClasses.margin1, baseClasses.roundCardFile)}>
											<CardHeader
												title={<Typography
													variant={"body2"}><b>{file.name?.substring(0, 20) + (file.name.length > 30 ? "..." : "")}</b></Typography>}
												style={{paddingBottom: 1}}
												action={<Tooltip title={languages[appContext.state.lang].TICKET_INPUT__REMOVE__FILES__}>
													<IconButton size={"small"}
													            onClick={(e) => removeFile(e, file)}>
														<DeleteTwoToneIcon/>
													</IconButton>
												</Tooltip>}
											/>
											<CardContent style={{padding: 0, maxHeight: 140, overflow: "hidden"}}>
												<embed
													width={"240px"}
													height={"140px"}
													type={file.type}
													title={file.name}
													src={URL.createObjectURL(file)}
													alt={file.name}
												/>
											</CardContent>
										</Card>
									)
								})}
							</div>
						)}
					</div>
				</div>
			</div>
			<Typography variant={"body2"} color={"textSecondary"} style={compact ? {fontSize: 11, marginTop: 4} : undefined}>
				{languages[appContext.state.lang].TICKET_INPUT_FILE_MAX_SIZE_LB}
			</Typography>
		</React.Fragment>
	);
}

export default React.memo(FileInput);
